<template>
  <div class="guides-quotes">
    <IconBase
      class="guides-quotes__icon"
      name="quote-icon"
    />
    <div class="guides-quotes-list">
      <GuideQuotesQuote
        v-for="quote in guidesQuotes"
        :key="quote.id"
        v-bind="quote"
        @guide-clicked="handleGuideClick"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, inject } from 'vue';

  import type { ArticleContent } from '@/features/articleContent';
  import type { PassionateGuide } from '@/shared/api/experience';
  import { mapApiGuideForQuotes } from '@/features/articleContent/lib/mapApiGuideForBlocks';
  import GuideQuotesQuote, { type Props as GuideQuote } from './Quote.vue';
  import { IconBase } from '@/shared/ui/iconBase';

  export type Guide = Omit<GuideQuote, 'title' | 'text'>;

  /** Строка — старый формат, массив — новый (tip-tap) */
  interface QuoteFromApi {
    guide_id?: number | string;
    id?: number | string;
    title?: ArticleContent | string;
    text?: ArticleContent | string;
  }

  interface Props {
    quotes?: QuoteFromApi[];
    guidesData?: Record<string, PassionateGuide>;
  }

  const props = defineProps<Props>();
  const sendGuideClickEvent = inject<((guideId: number, clickType: string) => void) | undefined>('sendGuideClickEvent');

  const handleGuideClick = (guideId: number) => {
    if (sendGuideClickEvent) {
      sendGuideClickEvent(guideId, 'comment');
    }
  };

  const emptyQuoteGuide = (guideId: number): Guide => ({
    id: guideId,
    name: '',
    avatarLink: '',
    location: '',
    experienceCount: 0,
  });

  const normalizeArticleContent = (value: ArticleContent | string | undefined): ArticleContent => {
    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      return [
        {
          type: 'paragraph',
          children: [{ type: 'text', raw: value }],
        },
      ] as ArticleContent;
    }

    return [];
  };

  const normalizedGuidesFromData = computed(() => {
    const guidesDataEntries = Object.entries(props.guidesData ?? {});

    return guidesDataEntries
      .map(([guideKey, guideData]) => {
        const guideIdFromValue = Number(guideData.id);
        const guideIdFromKey = Number(guideKey);
        const guideId = Number.isFinite(guideIdFromValue) ? guideIdFromValue : guideIdFromKey;

        if (!Number.isFinite(guideId)) {
          return null;
        }

        return {
          ...guideData,
          id: guideId,
        };
      })
      .filter((guideData): guideData is PassionateGuide & { id: number } => guideData !== null);
  });

  const guidesQuotes = computed(() => {
    const { quotes } = props;
    if (!quotes?.length) {
      return [];
    }

    const postList = normalizedGuidesFromData.value;
    const guidesById = new Map(postList.map((api) => [Number(api.id), mapApiGuideForQuotes(api)]));
    const result: GuideQuote[] = [];

    for (const quote of quotes) {
      const guideId = Number(quote.guide_id ?? quote.id);
      const title = normalizeArticleContent(quote.title);
      const text = normalizeArticleContent(quote.text);
      if (!guideId || !title.length || !text.length) {
        continue;
      }

      const base = guidesById.get(guideId) ?? emptyQuoteGuide(guideId);
      result.push({ ...base, title, text });
    }

    return result;
  });
</script>

<style lang="scss" scoped>
  .guides-quotes {
    display: flex;
    gap: var(--alias-space-20);
    align-items: flex-start;
    max-width: var(--width-content);
    margin-top: var(--alias-space-32);

    @include tablet {
      gap: var(--alias-space-16);
      margin-top: var(--alias-space-24);
    }
  }

  .guides-quotes__icon {
    display: block;
    flex-shrink: 0;
    overflow: visible;
    width: 32px;
    height: 28px;
  }

  .guides-quotes-list {
    display: flex;
    flex-direction: column;
    gap: var(--alias-space-24);
  }
</style>
